/*
 * Copyright 2025 Kridaprema
 */
package com.karyadina.ui

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.Help
import androidx.compose.material.icons.automirrored.filled.Note
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.karyadina.model.AppFeature
import com.karyadina.model.FeatureCategory
import com.karyadina.ui.screens.ImageResizerScreen
import com.karyadina.ui.components.AndroidPromotionBanner

import com.karyadina.ui.screens.flashcards.FlashcardsScreen
import com.karyadina.ui.screens.ImageToPdfScreen
import com.karyadina.ui.screens.PomodoroTimerScreen
import com.karyadina.ui.screens.PomodoroHistoryScreen
import com.karyadina.ui.screens.QuickNotesScreen
import com.karyadina.ui.screens.studyPlanner.StudyPlannerScreen
import com.karyadina.ui.screens.TaskTrackerScreen
import com.karyadina.ui.screens.OCRScannerScreen
import com.karyadina.ui.screens.SettingsDialog
import com.karyadina.ui.navigation.MainViewModel
import com.karyadina.ui.navigation.NavigationEvent

import com.karyadina.ui.navigation.ScreenHome
import com.karyadina.ui.navigation.ScreenFeature
import com.karyadina.ui.navigation.ScreenPomodoroHistory
import com.karyadina.ui.navigation.ScreenHelp
import com.karyadina.ui.screens.HelpScreen
import com.karyadina.ui.navigation.ScreenAbout
import com.karyadina.ui.navigation.ScreenOnboarding
import com.karyadina.ui.navigation.ScreenFreeSource
import com.karyadina.ui.screens.AboutScreen
import com.karyadina.ui.screens.OnboardingScreen

import com.karyadina.ui.theme.AppTheme
import com.karyadina.ui.theme.StudyTheme
import kotlinx.coroutines.delay

import com.karyadina.ui.util.BackHandler
import com.karyadina.ui.theme.StudyStrings
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun App(
    viewModel: MainViewModel = org.koin.compose.viewmodel.koinViewModel()
) {
    val state by viewModel.uiState.collectAsState()
    val dashboardStats by viewModel.dashboardStats.collectAsState()
    val preferences by viewModel.preferences.collectAsState()
    var showSettingsDialog by remember { mutableStateOf(false) }
    // var showAssistant by remember { mutableStateOf(false) }  // ⚠️ DISABLED: Agent feature disabled

    val themeColor = remember(preferences.userPersona, preferences.customTheme) {
        preferences.customTheme?.color ?: preferences.userPersona?.defaultTheme?.color
    }

    AppTheme(
        mode = preferences.themeMode,
        seedColor = themeColor
    ) {
        // Handle Back Navigation logic slightly differently per screen if needed
        val onBackToHome = { viewModel.onEvent(NavigationEvent.NavigateTo(ScreenHome)) }
        val onNavigateBack = { viewModel.onEvent(NavigationEvent.NavigateBack) }

        // Intercept system back button
        BackHandler(enabled = state.currentScreen !is ScreenHome) {
            onNavigateBack()
        }


        AnimatedContent(
            targetState = state.currentScreen,
            transitionSpec = {
                fadeIn(animationSpec = tween(300)) togetherWith fadeOut(animationSpec = tween(300))
            },
            label = stringResource(StudyStrings.ScreenTransition)
        ) { currentScreen ->
            when (currentScreen) {
                is ScreenHome -> MainApp(
                    onFeatureClick = { feature ->
                        viewModel.onEvent(NavigationEvent.NavigateTo(ScreenFeature(feature)))
                    },
                    onAboutClick = {
                        viewModel.onEvent(NavigationEvent.NavigateTo(ScreenAbout))
                    },
                    onHelpClick = {
                        viewModel.onEvent(NavigationEvent.NavigateTo(ScreenHelp))
                    },
                    onFreeSourceClick = {
                        viewModel.onEvent(NavigationEvent.NavigateTo(ScreenFreeSource))
                    },
                    onReviewClick = { viewModel.onEvent(NavigationEvent.ToggleWeeklyReview(true)) },
                    dashboardStats = dashboardStats,
                    preferences = preferences,
                    onSettingsClick = { showSettingsDialog = true }
                    // onAssistantClick = { showAssistant = !showAssistant }  // ⚠️ DISABLED: Agent feature disabled
                )

                is ScreenFeature -> {
                    // Feature Screens
                    when (currentScreen.feature) {
                        AppFeature.IMAGE_TO_PDF -> ImageToPdfScreen(onBackClick = onNavigateBack)
                        AppFeature.IMAGE_RESIZER -> ImageResizerScreen(onBackClick = onNavigateBack)

                        AppFeature.TRANSLATOR -> PlaceholderScreen(
                            title = stringResource(StudyStrings.TranslatorTitle),
                            message = stringResource(StudyStrings.TranslatorComingSoon),
                            onBackClick = onNavigateBack
                        )

                        AppFeature.POMODORO_TIMER -> PomodoroTimerScreen(
                            service = viewModel.pomodoroService,
                            onBackClick = onNavigateBack,
                            onHistoryClick = {
                                viewModel.onEvent(
                                    NavigationEvent.NavigateTo(
                                        ScreenPomodoroHistory
                                    )
                                )
                            }
                        )

                        AppFeature.TASK_TRACKER -> TaskTrackerScreen(onBackClick = onNavigateBack)
                        AppFeature.STUDY_PLANNER -> StudyPlannerScreen(onBackClick = onNavigateBack)
                        AppFeature.QUICK_NOTES -> QuickNotesScreen(onBackClick = onNavigateBack)
                        AppFeature.FLASHCARDS -> FlashcardsScreen(onBackClick = onNavigateBack)
                        AppFeature.MAGIC_TEXT_SCANNER -> OCRScannerScreen(
                            onBackClick = onNavigateBack,
                            onSaveToNote = {
                                viewModel.onEvent(
                                    NavigationEvent.NavigateTo(
                                        ScreenFeature(AppFeature.QUICK_NOTES)
                                    )
                                )
                            }
                        )
                    }
                }

                is ScreenPomodoroHistory -> PomodoroHistoryScreen(
                    service = viewModel.pomodoroService,
                    onBackClick = onNavigateBack
                )

                is ScreenAbout -> {
                    val uriHandler = LocalUriHandler.current
                    AboutScreen(
                        onBackClick = onNavigateBack,
                        onPrivacyPolicyClick = {
                            uriHandler.openUri("https://kridaprema.com/karyadina/privacy-policy")
                        },
                        onExploreWebClick = {
                            uriHandler.openUri("https://kridaprema.com/karyadina")
                        },
                        onPrivacyWebClick = {
                            uriHandler.openUri("https://kridaprema.com/karyadina/privacy-policy")
                        },
                        onOpenUrl = { url -> uriHandler.openUri(url) }
                    )
                }
                
                is ScreenHelp -> HelpScreen(
                    onBackClick = onNavigateBack
                )

                is ScreenOnboarding -> OnboardingScreen(
                    onPersonaSelected = { persona ->
                        viewModel.setUserPersona(persona)
                    }
                )

                is ScreenFreeSource -> com.karyadina.freesource.ui.FreeSourceScreen(
                    onNavigateBack = onNavigateBack
                )

                else -> {
                }
            }


            if (state.showWeeklyReview) {
                com.karyadina.ui.screens.WeeklyReviewDialog(
                    stats = viewModel.getWeeklyReviewStats(),
                    onDismiss = { viewModel.onEvent(NavigationEvent.ToggleWeeklyReview(false)) }
                )
            }

            if (showSettingsDialog) {
                SettingsDialog(
                    preferences = preferences,
                    onToggleDailyFocus = { viewModel.toggleDailyFocus(it) },
                    onToggleWeeklyReview = { viewModel.toggleWeeklyReview(it) },
                    onToggleShowAllTools = { viewModel.toggleShowAllTools(it) },
                    onToggleAlwaysShowDocTools = { viewModel.toggleAlwaysShowDocTools(it) },
                    onSetThemeMode = { viewModel.setThemeMode(it) },
                    onSetCustomTheme = { viewModel.setCustomTheme(it) },
                    onSetLanguage = { viewModel.setLanguage(it) },
                    onResetPersona = {
                        viewModel.resetPersona()
                        showSettingsDialog = false
                    },
                    onDismiss = { showSettingsDialog = false }
                )
            }
            
            /* ========================================
               AGENT CHAT OVERLAY - DISABLED
               Agent feature is disabled to reduce app size
               Set ENABLE_AGENT = true in AppModule.kt to re-enable
               ======================================== */
            
            // Global Assistant Overlay
            /*
            AnimatedVisibility(
                visible = showAssistant,
                enter = slideInVertically(initialOffsetY = { it }),
                exit = slideOutVertically(targetOffsetY = { it })
            ) {
                Box(Modifier.fillMaxSize().background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.3f))) {
                    com.karyadina.ui.components.AgentChatOverlay(
                        onDismiss = { showAssistant = false },
                        modifier = Modifier.align(Alignment.BottomCenter)
                    )
                }
            }
            */
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PlaceholderScreen(title: String, message: String, onBackClick: () -> Unit) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(title) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, stringResource(StudyStrings.Back))
                    }
                }
            )
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding),
            contentAlignment = Alignment.Center
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Icon(
                    Icons.Filled.Construction,
                    contentDescription = null,
                    modifier = Modifier.size(64.dp),
                    tint = MaterialTheme.colorScheme.primary
                )
                Text(
                    message,
                    style = MaterialTheme.typography.titleLarge,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MainApp(
    onFeatureClick: (AppFeature) -> Unit,
    onAboutClick: () -> Unit,
    onHelpClick: () -> Unit,
    onFreeSourceClick: () -> Unit,
    onReviewClick: () -> Unit,
    onSettingsClick: () -> Unit,
    // onAssistantClick: () -> Unit,  // ⚠️ DISABLED: Agent feature disabled
    dashboardStats: com.karyadina.ui.navigation.DashboardStats,
    preferences: com.karyadina.ui.navigation.DashboardPreferences
) {
    val scrollBehavior = TopAppBarDefaults.enterAlwaysScrollBehavior()
    var scrollProgress by remember { mutableStateOf(0f) }

    // Animate header background opacity based on scroll progress
    val headerBackgroundAlpha by animateFloatAsState(
        targetValue = (0.0f + (scrollProgress * 1.5f)).coerceIn(0f, 0.95f),
        animationSpec = tween(durationMillis = 300, easing = FastOutSlowInEasing),
        label = stringResource(StudyStrings.HeaderAlpha)
    )

    Scaffold(
        topBar = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(MaterialTheme.colorScheme.surface.copy(alpha = headerBackgroundAlpha))
            ) {
                CenterAlignedTopAppBar(
                    title = {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                stringResource(StudyStrings.AppName),
                                style = MaterialTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold
                            )
                            if (headerBackgroundAlpha < 0.5f) {
                                val subtitle = preferences.userPersona?.let { stringResource(StudyStrings.Workspace, getPersonaName(it)) } 
                                    ?: stringResource(StudyStrings.AppSubtitle)
                                Text(
                                    subtitle,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    },
                    colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                        containerColor = Color.Transparent,
                        scrolledContainerColor = Color.Transparent
                    ),
                    actions = {
                         /* ⚠️ DISABLED: Agent feature disabled
                         IconButton(onClick = onAssistantClick) {
                             Icon(Icons.Filled.SmartToy, contentDescription = "Assistant")
                         }
                         */
                        IconButton(onClick = onSettingsClick) {
                            Icon(Icons.Filled.Settings, contentDescription = stringResource(StudyStrings.SettingsDesc))
                        }
                        IconButton(onClick = onHelpClick) {
                            Icon(Icons.AutoMirrored.Filled.Help, contentDescription = stringResource(StudyStrings.HelpDesc))
                        }
                        IconButton(onClick = onAboutClick) {
                            Icon(Icons.Filled.Info, contentDescription = stringResource(StudyStrings.AboutDesc))
                        }
                    },
                    scrollBehavior = scrollBehavior
                )
            }
        },
        modifier = Modifier.nestedScroll(scrollBehavior.nestedScrollConnection)
    ) { paddingValues ->
        HomeScreen(
            modifier = Modifier.padding(paddingValues),
            onFeatureClick = onFeatureClick,
            onFreeSourceClick = onFreeSourceClick,
            onScrollProgress = { progress -> scrollProgress = progress },
            dashboardStats = dashboardStats,
            preferences = preferences,
            onReviewClick = onReviewClick
        )
    }
}

@Composable
fun HomeScreen(
    modifier: Modifier = Modifier,
    onFeatureClick: (AppFeature) -> Unit,
    onFreeSourceClick: () -> Unit,
    onScrollProgress: (Float) -> Unit = {},
    dashboardStats: com.karyadina.ui.navigation.DashboardStats,
    preferences: com.karyadina.ui.navigation.DashboardPreferences,
    onReviewClick: () -> Unit
) {
    var showWelcomeBanner by remember { mutableStateOf(true) }
    val listState = rememberLazyListState()

    // Auto-dismiss welcome banner after 5 seconds
    LaunchedEffect(Unit) {
        delay(5000)
        showWelcomeBanner = false
    }

    // Track scroll progress logic
    LaunchedEffect(listState) {
        snapshotFlow { listState.firstVisibleItemIndex to listState.firstVisibleItemScrollOffset }
            .collect { (index, offset) ->
                // If index > 0, we are past the start, so header should be fully opaque (1f)
                // If index == 0, we map the first 500px of offset to 0f..1f
                val progress = if (index > 0) 1f else (offset / 500f).coerceIn(0f, 1f)
                onScrollProgress(progress)
            }
    }

    com.karyadina.ui.components.ResponsiveContent {
    LazyColumn(
        state = listState,
        modifier = modifier
            .fillMaxSize()
            .background(
                Brush.verticalGradient(
                    colors = listOf(
                        MaterialTheme.colorScheme.surface,
                        MaterialTheme.colorScheme.surfaceContainerLowest
                    )
                )
            ),
        contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 8.dp, bottom = 32.dp),
        verticalArrangement = Arrangement.spacedBy(24.dp)
    ) {
        // 1. Welcome Banner
        if (showWelcomeBanner) {
            item(key = "welcome_banner") {
                AnimatedVisibility(
                    visible = showWelcomeBanner,
                    enter = fadeIn() + expandVertically(),
                    exit = fadeOut() + shrinkVertically()
                ) {
                    WelcomeBanner()
                }
            }
        }
        
        // 1.1 Android Promotion Banner (Web only)
        item(key = "android_promo_banner") {
            AndroidPromotionBanner()
        }
        // 1.2 Weekly Review Action
        if (preferences.showWeeklyReview) {
            item(key = "weekly_review") {
             OutlinedCard(
                onClick = onReviewClick,
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.outlinedCardColors(
                    containerColor = MaterialTheme.colorScheme.surface
                )
             ) {
                 Row(
                     modifier = Modifier.padding(16.dp),
                     verticalAlignment = Alignment.CenterVertically,
                     horizontalArrangement = Arrangement.spacedBy(16.dp)
                 ) {
                     Icon(Icons.Filled.DateRange, null, tint = MaterialTheme.colorScheme.primary)
                     Column(modifier = Modifier.weight(1f)) {
                         Text(stringResource(StudyStrings.WeeklyReviewTitle), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                         Text(stringResource(StudyStrings.WeeklyReviewCardDesc), style = MaterialTheme.typography.bodySmall)
                     }
                     Icon(Icons.AutoMirrored.Filled.ArrowForward, null, tint = MaterialTheme.colorScheme.onSurface.copy(alpha=0.5f))
                 }
             }
        }
        }
        
        // 1.5. Task Dashboard (Daily Focus)
        if (preferences.showDailyFocus && (dashboardStats.overdueCount > 0 || dashboardStats.todayCount > 0)) {
            item(key = "dashboard_focus") {
                ElevatedCard(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.elevatedCardColors(
                        containerColor = if (dashboardStats.overdueCount > 0) 
                            MaterialTheme.colorScheme.errorContainer 
                        else MaterialTheme.colorScheme.tertiaryContainer
                    )
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                if (dashboardStats.overdueCount > 0) Icons.Filled.Warning else Icons.Filled.Today,
                                null,
                                tint = if (dashboardStats.overdueCount > 0) 
                                    MaterialTheme.colorScheme.onErrorContainer 
                                else MaterialTheme.colorScheme.onTertiaryContainer
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                stringResource(StudyStrings.DailyFocusTitle),
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = if (dashboardStats.overdueCount > 0) 
                                    MaterialTheme.colorScheme.onErrorContainer 
                                else MaterialTheme.colorScheme.onTertiaryContainer
                            )
                        }
                        
                        Text(
                            buildString {
                                if (dashboardStats.overdueCount > 0) {
                                    append(stringResource(StudyStrings.DailyFocusOverdueFormat, dashboardStats.overdueCount))
                                    if (dashboardStats.todayCount > 0) append(stringResource(StudyStrings.DotSeparator))
                                }
                                if (dashboardStats.todayCount > 0) {
                                    append(stringResource(StudyStrings.DailyFocusDueTodayFormat, dashboardStats.todayCount))
                                }
                            },
                             style = MaterialTheme.typography.bodyLarge,
                             color = if (dashboardStats.overdueCount > 0) 
                                MaterialTheme.colorScheme.onErrorContainer 
                             else MaterialTheme.colorScheme.onTertiaryContainer
                        )
                    }
                }
            }
        }

        // 2. Productivity Section
        item(key = "productivity_section") {
            FeatureCategorySection(
                category = FeatureCategory.PRODUCTIVITY,
                onFeatureClick = onFeatureClick,
                userPersona = preferences.userPersona,
                showAllTools = preferences.showAllTools
            )
        }

        // 3. Document Tools Section
        item(key = "document_section") {
            FeatureCategorySection(
                category = FeatureCategory.DOCUMENT_TOOLS,
                onFeatureClick = onFeatureClick,
                userPersona = preferences.userPersona,
                showAllTools = preferences.showAllTools,
                alwaysShow = preferences.alwaysShowDocTools
            )
        }

        // 4. Developer Support Section
        item(key = "developer_support") {
            com.karyadina.ui.components.DonationCard()
        }

        // 5. Free Sources Library
        item(key = "free_source_library") {
            com.karyadina.freesource.ui.FreeSourceCard(
                onCardClick = onFreeSourceClick
            )
        }
    }
    }
}

@Composable
fun WelcomeBanner() {
    ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.primaryContainer
        ),
        elevation = CardDefaults.elevatedCardElevation(defaultElevation = 2.dp)
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Surface(
                modifier = Modifier.size(48.dp),
                shape = CircleShape,
                color = MaterialTheme.colorScheme.primary,
                tonalElevation = 4.dp
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = Icons.Filled.AutoAwesome,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onPrimary,
                        modifier = Modifier.size(24.dp)
                    )
                }
            }

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    stringResource(StudyStrings.WelcomeTitle),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onPrimaryContainer
                )
                Text(
                    stringResource(StudyStrings.WelcomeDesc),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onPrimaryContainer.copy(alpha = 0.8f)
                )
            }
        }
    }
}

@Composable
fun FeatureCategorySection(
    category: FeatureCategory,
    onFeatureClick: (AppFeature) -> Unit,
    userPersona: com.karyadina.model.UserPersona? = null,
    showAllTools: Boolean = false,
    alwaysShow: Boolean = false
) {
    // Sort features based on persona if available
    val features = if (userPersona != null) {
        val all = AppFeature.getByCategory(category)
        if (showAllTools || alwaysShow) { // Allow alwaysShow to bypass filter
            // Reorder: Priority features first, then others
            val priority = userPersona.priorityFeatures
            all.sortedBy { if (priority.contains(it)) 0 else 1 }
        } else {
            // Strict Filter: Only show priority features
            all.filter { userPersona.priorityFeatures.contains(it) }
        }
    } else {
        AppFeature.getByCategory(category)
    }

    // Filter for Web: Only specific tools allowed
    if (com.karyadina.util.isWeb) {
        val allowedFeatures = setOf(
            AppFeature.TASK_TRACKER,
            AppFeature.QUICK_NOTES,
            AppFeature.IMAGE_TO_PDF,
            AppFeature.IMAGE_RESIZER
        )
        // We must re-assign to a var if 'features' was a val, but here 'features' is val.
        // So we need to return or change checking logic. 
        // Let's filter the resulting list.
        val webFiltered = features.filter { it in allowedFeatures }
        if (webFiltered.isEmpty()) return
        
        // Use webFiltered for rendering
        renderFeatures(webFiltered, category, onFeatureClick)
        return
    }

    if (features.isEmpty()) return // Hide empty sections

    renderFeatures(features, category, onFeatureClick)
}

@Composable
fun renderFeatures(
    features: List<AppFeature>,
    category: FeatureCategory,
    onFeatureClick: (AppFeature) -> Unit
) {
    com.karyadina.ui.components.ResponsiveContent {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Section Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text(
                        text = getCategoryName(category),
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Text(
                        text = getCategoryDesc(category),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            // Feature Grid
            // Calculating height manually to simply wrapping, but Adaptive Grid is dynamic.
            // For now, simpler to use a fixed height container or let it wrap if not nesting scrollable (which we are).
            // Since we are inside a LazyColumn, nested LazyVerticalGrid is problematic unless we fix the height.
            // A better approach for LazyColumn nesting is using FlowRow or fixed height calculation based on adaptive.
            // HOWEVER, simple fix for "too big" is just adaptive columns.
            // Given the complexity of dynamic height with Adaptive grid inside LazyColumn, 
            // we will stick to calculating height BUT use a higher fixed column count for Web/Desktop if possible,
            // OR use FlowRow if available (experimental).
            // Let's stick to GridCells.Adaptive but we need to know the width to calc height... that's hard.
            // ALTERNATIVE: Use `FlowRow` style manually or just increase columns based on platform.
            
            // Let's use a workaround: On Web/Desktop, use Adaptive logic via BoxWithConstraints or just higher fixed count?
            // "Too big" implies we want smaller cards. 
            // Let's use GridCells.Adaptive(minSize = 160.dp) and rely on a simpler height heuristic or disable scrolling effectively.
            
            // Actually, nesting LazyVerticalGrid in LazyColumn is bad practice.
            // Ideally the parent should be the grid. But we have sections.
            // Let's use a safe height estimate for now or switch to FlowRow logic if we could.
            // But we must fix "Too Big" issue.
            
            // Let's try:
            val minSize = 160.dp
            // We can't easily calculate exact height for Adaptive without width. 
            // But if we are legally nesting, we can disable scrolling on the inner grid.
            // The height spec is required though.
            
            // To properly fix "Too big" without breaking scroll, let's use a "Responsive Grid" helper or
            // just hardcode 2 columns for mobile, 4 for others if we can detect width.
            // Since we are in Composable, BoxWithConstraints is best.
            
            BoxWithConstraints {
               val screenWidth = maxWidth
               val columns = if (screenWidth > 600.dp) 4 else 2
               
               // Recalculate height based on columns
               val rows = (features.size + columns - 1) / columns
               val itemHeight = 170.dp
               val spacing = 12.dp
               val gridHeight = (itemHeight * rows) + (spacing * (rows - 1).coerceAtLeast(0))
               
               Box(modifier = Modifier.height(gridHeight)) {
                    LazyVerticalGrid(
                        columns = GridCells.Fixed(columns),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        userScrollEnabled = false
                    ) {
                        items(features.size) { index ->
                            val feature = features[index]
                            val colorIndex = index + category.ordinal * 2
                            FeatureCard(feature = feature, colorIndex, onClick = { onFeatureClick(feature) })
                        }

                    }
               }
            }
        }
    }
}

@Composable
fun FeatureCard(feature: AppFeature, index: Int = 0, onClick: () -> Unit) {
    val gradientBrush = StudyTheme.getFeatureGradient(index)
    val contentColor = StudyTheme.getFeatureContentColor(index)

    Card(
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .height(170.dp),
        colors = CardDefaults.cardColors(
            containerColor = Color.Transparent,
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = 0.dp),
        shape = MaterialTheme.shapes.extraLarge
    ) {
           Box(modifier = Modifier
               .background(gradientBrush)) {
               Icon(

                   imageVector = getFeatureIcon(feature),
                   contentDescription = null,
                   tint = contentColor,
                   modifier = Modifier
                       .align(Alignment.BottomEnd)
                       .size(120.dp)
                       .offset(x = 30.dp, y = 30.dp) // Push it off-screen slightly
                       .alpha(0.15f), // Subtle fade
               )

                // Content Layout
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(20.dp), // More internal padding
                    verticalArrangement = Arrangement.SpaceBetween,
                    horizontalAlignment = Alignment.Start
                ) {
                    // Top: Icon
                    Surface(
                        shape = CircleShape,
                        // White/Surface background for the icon circle to make it pop against the gradient
                        color = MaterialTheme.colorScheme.surface.copy(alpha = 0.9f),
                        modifier = Modifier.size(48.dp),
                        shadowElevation = 2.dp
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(
                                imageVector = getFeatureIcon(feature),
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.primary, // Brand color for the icon
                                modifier = Modifier.size(24.dp)
                            )
                        }
                    }
                    // Text Content
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(
                            text = getFeatureName(feature),
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = contentColor,
                            maxLines = 1
                        )
                        Text(
                            text = getFeatureDesc(feature),
                            style = MaterialTheme.typography.bodySmall,
                            color = contentColor.copy(alpha = 0.8f),
                            maxLines = 2,
                            lineHeight = 16.sp,
                            overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis
                        )
                    }
                }

           }
    }
}

// Helper to get localized category name
@Composable
fun getCategoryName(category: FeatureCategory): String {
    return when (category) {
        FeatureCategory.DOCUMENT_TOOLS -> stringResource(StudyStrings.CatDocTitle)
        FeatureCategory.PRODUCTIVITY -> stringResource(StudyStrings.CatProdTitle)
        FeatureCategory.UTILITIES -> stringResource(StudyStrings.CatUtilTitle)
    }
}

// Helper to get localized category description
@Composable
fun getCategoryDesc(category: FeatureCategory): String {
    return when (category) {
        FeatureCategory.DOCUMENT_TOOLS -> stringResource(StudyStrings.CatDocDesc)
        FeatureCategory.PRODUCTIVITY -> stringResource(StudyStrings.CatProdDesc)
        FeatureCategory.UTILITIES -> stringResource(StudyStrings.CatUtilDesc)
    }
}

// Helper to get localized feature name
@Composable
fun getFeatureName(feature: AppFeature): String {
    return when (feature) {
        AppFeature.IMAGE_TO_PDF -> stringResource(StudyStrings.FeatureImg2PdfTitle)
        AppFeature.IMAGE_RESIZER -> stringResource(StudyStrings.FeatureResizerTitle)
        AppFeature.TRANSLATOR -> stringResource(StudyStrings.FeatureTranslatorTitle)
        AppFeature.MAGIC_TEXT_SCANNER -> stringResource(StudyStrings.FeatureScannerTitle)
        AppFeature.POMODORO_TIMER -> stringResource(StudyStrings.FeatureTimerTitle)
        AppFeature.TASK_TRACKER -> stringResource(StudyStrings.FeatureTrackerTitle)
        AppFeature.STUDY_PLANNER -> stringResource(StudyStrings.FeaturePlannerTitle)
        AppFeature.QUICK_NOTES -> stringResource(StudyStrings.FeatureNotesTitle)
        AppFeature.FLASHCARDS -> stringResource(StudyStrings.FeatureFlashcardsTitle)
    }
}

// Helper to get localized feature description
@Composable
fun getFeatureDesc(feature: AppFeature): String {
    return when (feature) {
        AppFeature.IMAGE_TO_PDF -> stringResource(StudyStrings.FeatureImg2PdfDesc)
        AppFeature.IMAGE_RESIZER -> stringResource(StudyStrings.FeatureResizerDesc)
        AppFeature.TRANSLATOR -> stringResource(StudyStrings.FeatureTranslatorDesc)
        AppFeature.MAGIC_TEXT_SCANNER -> stringResource(StudyStrings.FeatureScannerDesc)
        AppFeature.POMODORO_TIMER -> stringResource(StudyStrings.FeatureTimerDesc)
        AppFeature.TASK_TRACKER -> stringResource(StudyStrings.FeatureTrackerDesc)
        AppFeature.STUDY_PLANNER -> stringResource(StudyStrings.FeaturePlannerDesc)
        AppFeature.QUICK_NOTES -> stringResource(StudyStrings.FeatureNotesDesc)
        AppFeature.FLASHCARDS -> stringResource(StudyStrings.FeatureFlashcardsDesc)
    }
}

// Helper to map enum to icons

fun getFeatureIcon(feature: AppFeature): androidx.compose.ui.graphics.vector.ImageVector {
    return when (feature) {
        AppFeature.IMAGE_TO_PDF -> Icons.Filled.PictureAsPdf
        AppFeature.IMAGE_RESIZER -> Icons.Filled.AspectRatio
        AppFeature.POMODORO_TIMER -> Icons.Filled.Timer
        AppFeature.TASK_TRACKER -> Icons.Filled.CheckCircle
        AppFeature.STUDY_PLANNER -> Icons.Filled.CalendarMonth
        AppFeature.QUICK_NOTES -> Icons.AutoMirrored.Filled.Note
        AppFeature.FLASHCARDS -> Icons.Filled.Style
        AppFeature.TRANSLATOR -> Icons.Filled.Translate
        AppFeature.MAGIC_TEXT_SCANNER -> Icons.Filled.DocumentScanner
    }


}

// Helper to get localized persona name
@Composable
fun getPersonaName(persona: com.karyadina.model.UserPersona): String {
    return when (persona) {
        com.karyadina.model.UserPersona.STUDENT -> stringResource(StudyStrings.PersonaStudentName)
        com.karyadina.model.UserPersona.PROFESSIONAL -> stringResource(StudyStrings.PersonaProfessionalName)
        com.karyadina.model.UserPersona.DEVELOPER -> stringResource(StudyStrings.PersonaDeveloperName)
        com.karyadina.model.UserPersona.CONTENT_CREATOR -> stringResource(StudyStrings.PersonaCreatorName)
        com.karyadina.model.UserPersona.RESEARCHER -> stringResource(StudyStrings.PersonaResearcherName)
        com.karyadina.model.UserPersona.GENERAL -> stringResource(StudyStrings.PersonaGeneralName)
    }
}

// Helper to get localized persona description
@Composable
fun getPersonaDescription(persona: com.karyadina.model.UserPersona): String {
     return when (persona) {
        com.karyadina.model.UserPersona.STUDENT -> stringResource(StudyStrings.PersonaStudentDesc)
        com.karyadina.model.UserPersona.PROFESSIONAL -> stringResource(StudyStrings.PersonaProfessionalDesc)
        com.karyadina.model.UserPersona.DEVELOPER -> stringResource(StudyStrings.PersonaDeveloperDesc)
        com.karyadina.model.UserPersona.CONTENT_CREATOR -> stringResource(StudyStrings.PersonaCreatorDesc)
        com.karyadina.model.UserPersona.RESEARCHER -> stringResource(StudyStrings.PersonaResearcherDesc)
        com.karyadina.model.UserPersona.GENERAL -> stringResource(StudyStrings.PersonaGeneralDesc)
    }
}
